import { Text as TextChakra } from '@chakra-ui/react';
import { SystemStyleObject } from '@chakra-ui/styled-system';
import * as CSS from 'csstype';
import { useMemo } from 'react';

interface TextProps extends React.PropsWithChildren {
  color?: string;
  textType?: TextType;
  textVariant?: TextVariant;
  textAlign?: CSS.Property.TextAlign;
  marginBottom?: string[] | string;
  marginTop?: string[] | string;
  fontWeight?: number;
  opacity?: number;
  customSx?: SystemStyleObject;
  ellipsis?: boolean;
  underline?: boolean;
  className?: string;
  style?: CSS.Properties;
}

type TextVariant =
  | 'primary'
  | 'secondary'
  | 'disabled'
  | 'link'
  | 'danger'
  | 'success';

type TextType = 'lg' | 'regular' | 'sm' | 'xs' | 'xxs';

const textColor = 'text.brand';

const getTextVariant = (textVariant?: TextVariant) => {
  switch (textVariant) {
    case 'primary':
      return {
        color: 'text.brand',
        opacity: 1,
      };
    case 'secondary':
      return {
        color: 'text.brand',
        opacity: 0.7,
      };
    case 'disabled':
      return {
        color: 'text.brand',
        opacity: 0.5,
      };
    case 'link':
      return {
        color: 'orange.900',
        opacity: 1,
      };
    case 'danger':
      return {
        color: 'red.600', //#BA2E1B
        opacity: 1,
      };
    case 'success':
      return {
        color: 'success.700', //#007855
        opacity: 1,
      };
    default:
      return {};
  }
};

const getLineHeight = (textType: TextType) => {
  switch (textType) {
    case 'lg':
      return '2rem';
    case 'regular':
      return ['1.25rem', '2rem'];
    case 'sm':
      return ['1.25rem', '1.5rem'];
    case 'xxs':
      return ['0.875rem', '0.875rem'];
    case 'xs':
    default:
      return ['1rem', '1.25rem'];
  }
};

const getFontSize = (textType: TextType) => {
  switch (textType) {
    case 'regular':
      return ['18px', '20px'];
    case 'sm':
      return '16px';
    case 'xs':
      return '14px';
    case 'xxs':
      return '12px';
    case 'lg':
      return '24px';
    default:
      return '20px';
  }
};

export const Text = ({
  children,
  textAlign,
  marginBottom,
  marginTop,
  textType = 'regular',
  color = textColor,
  fontWeight = 400,
  opacity,
  customSx = {},
  ellipsis = false,
  underline = false,
  textVariant,
  className,
  style,
}: TextProps) => {
  const ellipsesProps = useMemo(() => {
    if (!ellipsis) {
      return {};
    }
    return {
      whiteSpace: 'nowrap' as any,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    } as any;
  }, [ellipsis]);

  const opacityToApply = useMemo(() => {
    if (opacity !== undefined && opacity >= 0) {
      return opacity;
    }

    const variant = getTextVariant(textVariant);
    if (variant?.opacity !== undefined && variant?.opacity >= 0) {
      return variant.opacity;
    }

    return 1;
  }, [opacity, textVariant]);
  return (
    <TextChakra
      {...getTextVariant(textVariant)}
      className={className}
      fontSize={getFontSize(textType)}
      lineHeight={getLineHeight(textType)}
      fontFamily="Plus Jakarta Sans"
      color={color}
      marginBottom={marginBottom}
      marginTop={marginTop}
      textAlign={textAlign}
      fontWeight={fontWeight}
      opacity={opacityToApply}
      textDecoration={underline ? 'underline' : 'none'}
      sx={customSx}
      {...ellipsesProps}
      {...style}
    >
      {children}
    </TextChakra>
  );
};
